import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveComplaintAsCase,
  closeComplaintAsCase,
  createComplaintAsCase,
  deleteComplaintAsCase,
  fetchComplaintAsCase,
  fetchComplaintAsCases,
  reopenComplaintAsCase,
  revokeComplaintAsCase,
  updateComplaintAsCase,
  downloadComplaintAsPdf,
} from '../../api/complaintAs';
import { notifyError, reportError } from '../../utils/notify';
import { useCompanyStore } from '../../store/companyStore';
import { canConfirmInspection } from '../../types/companyRole';
import { APPROVAL_TARGET_COMPLAINT_AS } from '../../types/approval';
import {
  COMPLAINT_AS_STATUS_OPTIONS,
  COMPLAINT_AS_TYPE_OPTIONS,
  ComplaintAsCase,
  ComplaintAsCasePayload,
  complaintAsStatusLabel,
  complaintAsTypeLabel,
} from '../../types/complaintAs';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';

import '../contract/ContractPanel.css';
import '../product/InspectionFormSheet.css';
import '../product/NonconformanceReportPanel.css';
import './ComplaintAsPanel.css';

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; caseId: number }
  | { type: 'edit'; caseId: number };

const LIST_VIEW: View = { type: 'list' };

const EMPTY: ComplaintAsCasePayload = {
  caseNo: '',
  receivedDate: new Date().toISOString().slice(0, 10),
  caseType: 'COMPLAINT',
  customerName: '',
  customerContact: '',
  productName: '',
  productSpec: '',
  lotNo: '',
  qty: '',
  content: '',
  cause: '',
  actionText: '',
  actionResult: '',
  handlerName: '',
  status: 'RECEIVED',
  remark: '',
};

function isApproved(status?: string | null) {
  return (status || '').toUpperCase() === 'APPROVED';
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ['작성', authorName],
          ['검토', reviewerName],
          ['승인', approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span style={{ color: value ? '#1f2a37' : '#98a2b3', fontWeight: value ? 600 : 400 }}>
            {value || '-'}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({
  status,
  approvalInProgress,
}: {
  status?: string | null;
  approvalInProgress?: boolean | null;
}) {
  const key = approvalInProgress
    ? 'pending'
    : (status || 'RECEIVED').toLowerCase().replace('_', '-');
  return (
    <span className={`cas-status is-${key}`}>
      {complaintAsStatusLabel(status, approvalInProgress)}
    </span>
  );
}

function ComplaintAsPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [view, setView] = useState<View>(LIST_VIEW);
  const [rows, setRows] = useState<ComplaintAsCase[]>([]);
  const [detail, setDetail] = useState<ComplaintAsCase | null>(null);
  const [form, setForm] = useState<ComplaintAsCasePayload>(EMPTY);
  const [keyword, setKeyword] = useState('');
  const [caseNoHint, setCaseNoHint] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'ALL' | 'RECEIVED' | 'IN_PROGRESS' | 'CLOSED'
  >('ALL');

  const summary = useMemo(() => {
    const received = rows.filter((r) => r.status === 'RECEIVED').length;
    const inProgress = rows.filter((r) => r.status === 'IN_PROGRESS').length;
    const closed = rows.filter((r) => r.status === 'CLOSED').length;
    return { total: rows.length, received, inProgress, closed };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'ALL') return rows;
    return rows.filter((row) => (row.status || 'RECEIVED') === statusFilter);
  }, [rows, statusFilter]);

  const loadList = useCallback(async (q?: string) => {
    try {
      setLoading(true);
      setRows(await fetchComplaintAsCases(q));
      setError('');
    } catch {
      reportError(setError, '불만/AS 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setView(LIST_VIEW);
    setKeyword('');
    setStatusFilter('ALL');
    setCaseNoHint('');
    void loadList();
  }, [companyId, loadList, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      void loadList(keyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [keyword, loadList, view.type]);

  useEffect(() => {
    if (view.type !== 'detail' && view.type !== 'edit') {
      setDetail(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const row = await fetchComplaintAsCase(view.caseId);
        if (cancelled) return;
        setDetail(row);
        if (view.type === 'edit') {
          if (row.status === 'CLOSED') {
            setView({ type: 'detail', caseId: row.caseId });
            return;
          }
          setForm({
            caseNo: row.caseNo || '',
            receivedDate: row.receivedDate || new Date().toISOString().slice(0, 10),
            caseType: (row.caseType as ComplaintAsCasePayload['caseType']) || 'COMPLAINT',
            customerName: row.customerName || '',
            customerContact: row.customerContact || '',
            productName: row.productName || '',
            productSpec: row.productSpec || '',
            lotNo: row.lotNo || '',
            qty: row.qty || '',
            content: row.content || '',
            cause: row.cause || '',
            actionText: row.actionText || '',
            actionResult: row.actionResult || '',
            handlerName: row.handlerName || '',
            status: (row.status as ComplaintAsCasePayload['status']) || 'RECEIVED',
            remark: row.remark || '',
          });
        }
      } catch {
        if (!cancelled) reportError(setError, '상세를 불러오지 못했습니다.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view]);

  const goList = () => {
    setView(LIST_VIEW);
    setError('');
  };

  const openCreate = () => {
    setForm(EMPTY);
    setCaseNoHint('저장 시 접수번호가 자동 발급됩니다.');
    setError('');
    setView({ type: 'create' });
  };

  const patch = (key: keyof ComplaintAsCasePayload, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.content.trim()) {
      reportError(setError, '접수 내용을 입력해 주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (view.type === 'edit') {
        const payload: ComplaintAsCasePayload = { ...form };
        // 승인 후 조치 보완 시 상태값은 서버에서 유지
        if (isApproved(form.status)) {
          delete payload.status;
        }
        const updated = await updateComplaintAsCase(view.caseId, payload);
        setDetail(updated);
        setView({ type: 'detail', caseId: updated.caseId });
      } else {
        const created = await createComplaintAsCase(form);
        window.alert(`접수번호 ${created.caseNo || '-'}로 등록되었습니다.`);
        setDetail(created);
        setView({ type: 'detail', caseId: created.caseId });
      }
      await loadList(keyword);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (!detail) return;
    if ((detail.status || '').toUpperCase() !== 'APPROVED') {
      reportError(setError, '승인 완료된 건만 종결할 수 있습니다. 먼저 결재·승인을 진행해 주세요.');
      return;
    }
    if (!window.confirm('조치를 반영한 뒤 이 건을 종결할까요?')) return;
    setSaving(true);
    try {
      const updated = await closeComplaintAsCase(detail.caseId);
      setDetail(updated);
      await loadList(keyword);
    } catch {
      reportError(setError, '종결 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async () => {
    if (!detail) return;
    if ((detail.status || '').toUpperCase() !== 'CLOSED') {
      reportError(setError, '종결된 건만 재개할 수 있습니다.');
      return;
    }
    if (!window.confirm('종결을 취소하고 승인 상태로 되돌릴까요?')) return;
    setSaving(true);
    try {
      const updated = await reopenComplaintAsCase(detail.caseId);
      setDetail(updated);
      await loadList(keyword);
    } catch {
      reportError(setError, '재개 처리에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detail) return;
    if (isApproved(detail.status)) {
      reportError(setError, '승인 완료된 건은 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm('이 건을 삭제할까요?')) return;
    setSaving(true);
    try {
      await deleteComplaintAsCase(detail.caseId);
      goList();
      await loadList(keyword);
    } catch {
      reportError(setError, '삭제에 실패했습니다. 종결된 건은 삭제할 수 없습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    if (!canApproveDirect) {
      reportError(setError, '바로 승인은 오너/팀장만 가능합니다.');
      return;
    }
    if (
      !window.confirm(
        '불만/AS 건을 바로 승인할까요? 승인 후 조치 내용을 보완하고 종결할 수 있습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const saved = await approveComplaintAsCase(detail.caseId);
      setDetail(saved);
      setApprovalMessage('바로 승인되었습니다.');
      await loadList(keyword);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '바로 승인에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail) return;
    if (!window.confirm('승인을 철회할까요?\n승인란·검토란이 지워집니다.')) return;
    try {
      setApproving(true);
      setError('');
      const saved = await revokeComplaintAsCase(detail.caseId);
      setDetail(saved);
      setApprovalMessage('승인이 철회되었습니다.');
      await loadList(keyword);
    } catch (err: unknown) {
      reportError(setError, (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.');
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      const name = detail.caseNo?.trim() || String(detail.caseId);
      await downloadComplaintAsPdf(detail.caseId, `불만AS_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const renderEditActions = () => (
    <div className="ncr-form__actions">
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          view.type === 'edit' ? setView({ type: 'detail', caseId: view.caseId }) : goList()
        }
      >
        취소
      </button>
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );

  const renderDetailActions = () => (
    <div className="ncr-actions">
      {isApproved(detail?.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
        </button>
      ) : null}
      {detail?.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? '철회 중...' : '승인 철회'}
        </button>
      ) : null}
      {detail?.status === 'CLOSED' ? (
        <button
          type="button"
          className="ncr-btn is-secondary"
          disabled={saving}
          onClick={() => void handleReopen()}
        >
          재개
        </button>
      ) : null}
      {detail &&
      detail.status !== 'CLOSED' &&
      detail.canEdit !== false &&
      !detail.approvalInProgress ? (
        <button
          type="button"
          className="ncr-btn is-secondary"
          onClick={() => setView({ type: 'edit', caseId: detail.caseId })}
        >
          수정
        </button>
      ) : null}
      {detail?.canCancelApproval ? (
        <ApprovalCancelButton
          detail={detail}
          className="ncr-btn is-secondary"
          disabled={approving}
          onCancelled={async () => {
            setDetail(await fetchComplaintAsCase(detail.caseId));
            setApprovalMessage('');
            void loadList();
          }}
        />
      ) : null}
      {detail &&
      detail.status !== 'CLOSED' &&
      detail.status !== 'APPROVED' &&
      !isApproved(detail.status) &&
      !detail.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage('');
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void handleApprove()}
            >
              {approving ? '승인 중...' : '승인'}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => void handleDelete()}
          >
            삭제
          </button>
        </>
      ) : null}
      {detail?.canClose || isApproved(detail?.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={saving}
          onClick={() => void handleClose()}
        >
          종결
        </button>
      ) : null}
      <button type="button" className="ncr-btn is-secondary" onClick={goList}>
        목록으로
      </button>
    </div>
  );

  const renderForm = () => (
    <form className="ncr-form" onSubmit={handleSubmit}>
      {renderEditActions()}
      <section className="ncr-form__section">
        <h3>기본 정보</h3>
        <div className="ncr-form__grid">
          <label>
            접수번호
            <input
              value={
                view.type === 'create'
                  ? ''
                  : form.caseNo || ''
              }
              readOnly
              placeholder={view.type === 'create' ? '저장 시 자동 발급' : undefined}
              maxLength={100}
            />
            {view.type === 'create' && caseNoHint ? (
              <span className="ncr-form__hint">{caseNoHint}</span>
            ) : null}
          </label>
          <label>
            접수일
            <input
              type="date"
              value={form.receivedDate || ''}
              onChange={(e) => patch('receivedDate', e.target.value)}
            />
          </label>
          <label>
            유형
            <select
              value={form.caseType || 'COMPLAINT'}
              onChange={(e) => patch('caseType', e.target.value)}
            >
              {COMPLAINT_AS_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            상태
            <select
              value={isApproved(form.status) ? 'APPROVED' : form.status || 'RECEIVED'}
              disabled={isApproved(form.status)}
              onChange={(e) => patch('status', e.target.value)}
            >
              {isApproved(form.status) ? (
                <option value="APPROVED">승인</option>
              ) : (
                COMPLAINT_AS_STATUS_OPTIONS.filter(
                  (o) => o.value !== 'CLOSED' && o.value !== 'APPROVED'
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              )}
            </select>
          </label>
          <label>
            담당자
            <input
              value={form.handlerName || ''}
              onChange={(e) => patch('handlerName', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="ncr-form__section">
        <h3>고객·제품</h3>
        <div className="ncr-form__grid">
          <label>
            고객명
            <input
              value={form.customerName || ''}
              onChange={(e) => patch('customerName', e.target.value)}
            />
          </label>
          <label>
            연락처
            <input
              value={form.customerContact || ''}
              onChange={(e) => patch('customerContact', e.target.value)}
            />
          </label>
          <label>
            제품명
            <input
              value={form.productName || ''}
              onChange={(e) => patch('productName', e.target.value)}
            />
          </label>
          <label>
            규격
            <input
              value={form.productSpec || ''}
              onChange={(e) => patch('productSpec', e.target.value)}
            />
          </label>
          <label>
            LOT
            <input value={form.lotNo || ''} onChange={(e) => patch('lotNo', e.target.value)} />
          </label>
          <label>
            수량
            <input value={form.qty || ''} onChange={(e) => patch('qty', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="ncr-form__section">
        <h3>내용·조치</h3>
        <p>접수 내용과 원인·조치·결과를 기록합니다.</p>
        <div className="ncr-form__grid">
          <label className="is-span-2">
            접수 내용 *
            <textarea
              rows={4}
              value={form.content}
              onChange={(e) => patch('content', e.target.value)}
              required
            />
          </label>
          <label className="is-span-2">
            원인
            <textarea
              rows={3}
              value={form.cause || ''}
              onChange={(e) => patch('cause', e.target.value)}
            />
          </label>
          <label className="is-span-2">
            조치 내용
            <textarea
              rows={3}
              value={form.actionText || ''}
              onChange={(e) => patch('actionText', e.target.value)}
            />
          </label>
          <label className="is-span-2">
            조치 결과
            <textarea
              rows={3}
              value={form.actionResult || ''}
              onChange={(e) => patch('actionResult', e.target.value)}
            />
          </label>
          <label className="is-span-2">
            비고
            <textarea
              rows={2}
              value={form.remark || ''}
              onChange={(e) => patch('remark', e.target.value)}
            />
          </label>
        </div>
      </section>

      {renderEditActions()}
    </form>
  );

  return (
    <main className="contract-panel ncr-panel cas-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>불만 및 AS</strong>
          <span>고객 불만 · A/S 처리</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="번호 / 고객 / 제품 / 내용"
          />
        </div>
        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__hint">불러오는 중...</p>
          ) : filteredRows.length === 0 ? (
            <p className="contract-panel__hint">해당 조건의 건이 없습니다.</p>
          ) : (
            <ul className="contract-panel__list">
              {filteredRows.map((row) => (
                <li key={row.caseId}>
                  <button
                    type="button"
                    className={`contract-panel__list-item${
                      (view.type === 'detail' || view.type === 'edit') &&
                      view.caseId === row.caseId
                        ? ' is-active'
                        : ''
                    }`}
                    onClick={() => setView({ type: 'detail', caseId: row.caseId })}
                  >
                    <span className="contract-panel__list-name">
                      {row.customerName || row.productName || '제목 없음'}
                    </span>
                    <span className="contract-panel__list-meta">
                      {row.caseNo ? `${row.caseNo} · ` : ''}
                      {complaintAsTypeLabel(row.caseType)} · <StatusBadge status={row.status} approvalInProgress={row.approvalInProgress} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__main">
        {view.type === 'create' || view.type === 'edit' ? (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>{view.type === 'edit' ? '불만/AS 수정' : '불만/AS 접수'}</h2>
                <p>접수 → 결재/승인 → 조치 → 종결 순으로 진행합니다.</p>
              </div>
              <button type="button" className="ncr-btn is-secondary" onClick={goList}>
                목록으로
              </button>
            </div>
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">{renderForm()}</div>
          </>
        ) : view.type === 'detail' ? (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>불만/AS 상세</h2>
                <p>
                  {detail?.caseNo || '-'} · <StatusBadge status={detail?.status} approvalInProgress={detail?.approvalInProgress} />
                </p>
              </div>
              {renderDetailActions()}
            </div>
            {approvalMessage ? (
              <p className="contract-panel__hint" style={{ color: '#1a5fb4' }}>
                {approvalMessage}
              </p>
            ) : null}
            {detail?.approvalInProgress ? (
              <p className="contract-panel__hint">
                결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
              </p>
            ) : null}
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">
              {!detail ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : (
                <div className="ncr-detail">
                  <div
                    className="ifs-sheet"
                    style={{ marginBottom: 14, padding: '12px 14px', background: '#fff' }}
                  >
                    <div className="ifs-sheet__top" style={{ marginBottom: 0 }}>
                      <div>
                        <h4 className="ifs-sheet__title" style={{ margin: 0, fontSize: 15 }}>
                          {detail.customerName || detail.productName || '불만/AS'}
                        </h4>
                        <p className="ifs-sheet__subtitle" style={{ margin: '4px 0 0' }}>
                          {detail.caseNo || '-'} · {complaintAsStatusLabel(detail.status, detail.approvalInProgress)}
                        </p>
                      </div>
                      <StampBox
                        authorName={detail.authorName || detail.handlerName}
                        reviewerName={detail.reviewerName}
                        approverName={detail.approverName}
                      />
                    </div>
                  </div>
                  <section className="ncr-detail__card">
                    <h3>기본 정보</h3>
                    <dl>
                      <dt>접수번호</dt>
                      <dd>{detail.caseNo || '-'}</dd>
                      <dt>접수일</dt>
                      <dd>{detail.receivedDate || '-'}</dd>
                      <dt>유형</dt>
                      <dd>{complaintAsTypeLabel(detail.caseType)}</dd>
                      <dt>상태</dt>
                      <dd>
                        <StatusBadge status={detail.status} approvalInProgress={detail.approvalInProgress} />
                      </dd>
                      <dt>담당자</dt>
                      <dd>{detail.handlerName || '-'}</dd>
                    </dl>
                  </section>
                  <section className="ncr-detail__card">
                    <h3>고객·제품</h3>
                    <dl>
                      <dt>고객명</dt>
                      <dd>{detail.customerName || '-'}</dd>
                      <dt>연락처</dt>
                      <dd>{detail.customerContact || '-'}</dd>
                      <dt>제품명</dt>
                      <dd>{detail.productName || '-'}</dd>
                      <dt>규격</dt>
                      <dd>{detail.productSpec || '-'}</dd>
                      <dt>LOT</dt>
                      <dd>{detail.lotNo || '-'}</dd>
                      <dt>수량</dt>
                      <dd>{detail.qty || '-'}</dd>
                    </dl>
                  </section>
                  <section className="ncr-detail__card is-wide">
                    <h3>내용·조치</h3>
                    <dl>
                      <dt>접수 내용</dt>
                      <dd>{detail.content || '-'}</dd>
                      <dt>원인</dt>
                      <dd>{detail.cause || '-'}</dd>
                      <dt>조치 내용</dt>
                      <dd>{detail.actionText || '-'}</dd>
                      <dt>조치 결과</dt>
                      <dd>{detail.actionResult || '-'}</dd>
                      <dt>비고</dt>
                      <dd>{detail.remark || '-'}</dd>
                    </dl>
                  </section>
                </div>
              )}
            </div>
            {renderDetailActions()}
            {detail ? (
              <ApprovalRequestModal
                open={approvalOpen}
                titleDefault={`${detail.customerName || detail.productName || '불만/AS'} 결재`}
                targetType={APPROVAL_TARGET_COMPLAINT_AS}
                targetId={detail.caseId}
                onClose={() => setApprovalOpen(false)}
                onSubmitted={() => {
                  setApprovalOpen(false);
                  setApprovalMessage(
                    '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
                  );
                  void fetchComplaintAsCase(detail.caseId).then(setDetail);
                  void loadList(keyword);
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>불만 및 AS 처리</h2>
                <p>고객 불만·A/S를 접수·결재한 뒤 조치하고 종결합니다.</p>
              </div>
              <button type="button" className="ncr-btn is-primary" onClick={() => void openCreate()}>
                신규 접수
              </button>
            </div>
            {error && <p className="contract-panel__error">{error}</p>}
            <div className="contract-panel__content">
              <div className="ncr-summary">
                <button
                  type="button"
                  className={`ncr-summary__chip${statusFilter === 'ALL' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('ALL')}
                >
                  <strong>{summary.total}</strong>
                  <span>전체</span>
                </button>
                <button
                  type="button"
                  className={`ncr-summary__chip${statusFilter === 'RECEIVED' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('RECEIVED')}
                >
                  <strong>{summary.received}</strong>
                  <span>접수</span>
                </button>
                <button
                  type="button"
                  className={`ncr-summary__chip${
                    statusFilter === 'IN_PROGRESS' ? ' is-active' : ''
                  }`}
                  onClick={() => setStatusFilter('IN_PROGRESS')}
                >
                  <strong>{summary.inProgress}</strong>
                  <span>처리중</span>
                </button>
                <button
                  type="button"
                  className={`ncr-summary__chip${statusFilter === 'CLOSED' ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter('CLOSED')}
                >
                  <strong>{summary.closed}</strong>
                  <span>종결</span>
                </button>
              </div>

              <div className="cas-table-wrap">
                <table className="cas-table">
                  <thead>
                    <tr>
                      <th>접수번호</th>
                      <th>접수일</th>
                      <th>유형</th>
                      <th>고객</th>
                      <th>제품</th>
                      <th>담당</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7}>불러오는 중...</td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={7}>등록된 건이 없습니다.</td>
                      </tr>
                    ) : (
                      filteredRows.map((row) => (
                        <tr
                          key={row.caseId}
                          onClick={() => setView({ type: 'detail', caseId: row.caseId })}
                        >
                          <td>{row.caseNo || '-'}</td>
                          <td>{row.receivedDate || '-'}</td>
                          <td>{complaintAsTypeLabel(row.caseType)}</td>
                          <td>{row.customerName || '-'}</td>
                          <td>{row.productName || '-'}</td>
                          <td>{row.handlerName || '-'}</td>
                          <td>
                            <StatusBadge status={row.status} approvalInProgress={row.approvalInProgress} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default ComplaintAsPanel;
